import { gql } from '@/utilities/gql';
import type { ResultOf } from '@/utilities/gql';
import type { QueryFunctionContext } from '@tanstack/react-query';
import type { PrimaryCountryISO } from '@/constants/countries';
import { gqlClient } from '@/utilities/graphQLClient';
import { countries } from '@/constants/countries';
import { format } from 'date-fns';
import {
  formatterFactor,
  formatCurrency,
} from '@/pages/Market/utils/formatter';

const QUERY = gql(`
  query timeseries(
    $market: String!
    $industryId: Float!
    $startTime: Float!
    $endTime: Float!
  ) {
    IndustryTimeseries(
      criteria: {
        market: $market
        industry: $industryId
        startUnix: $startTime
        endUnix: $endTime
      }
    ) {
      industry {
        name
      }
      market {
        iso2
      }
      pe
      ps
      absolutePE
      earnings
      revenue
      marketCap
      averages
    }
  }
`);

type IndustryTimeSeries = NonNullable<
  ResultOf<typeof QUERY>
>['IndustryTimeseries'];

export type PeriodType = '1Y' | '3Y' | '5Y' | '10Y';
export type ValuationResponseType = 'ps' | 'pe' | 'absolutePE';

interface ChartData {
  time: number;
  dateFormatted: string;
  medianPE: number;
  medianPEFormatted: string;
  ps: number;
  psFormatted: string;
  absolutePE: number;
  absolutePEFormatted: string;
  earnings: number;
  earningsDiff: number;
  earningsFormatted: string;
  revenue: number;
  revenueDiff: number;
  revenueFormatted: string;
  marketCap: number;
  marketCapDiff: number;
  marketCapFormatted: string;
}

type PeriodAverageResponse = Record<PeriodType, number>;
type ValuationType = 'ps' | 'medianPE' | 'absolutePE';

export interface Response {
  chartData: ChartData[];
  industry: string | undefined;
  market: string;
  averages: Record<ValuationType, PeriodAverageResponse>;
}

function calculateDiffPercentage(num: number, per: number) {
  if (per !== 0) {
    return ((num - per) / Math.abs(per)) * 100;
  }
  return 0;
}

type Average = {
  weighted_avg_1y: number;
  weighted_avg_3y: number;
  weighted_avg_5y: number;
  weighted_avg_10y: number;
};

const isAverage = (data: unknown): data is Average => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  return (
    'weighted_avg_1y' in data &&
    'weighted_avg_3y' in data &&
    'weighted_avg_5y' in data &&
    'weighted_avg_10y' in data
  );
};

const defaultAverages: Average = {
  weighted_avg_1y: 0,
  weighted_avg_3y: 0,
  weighted_avg_5y: 0,
  weighted_avg_10y: 0,
};

export const responseTransformer = (
  timeseries: IndustryTimeSeries
): Response => {
  const currencyISO =
    countries[timeseries.market.iso2.toLowerCase()].currencyISO;

  const { ps, pe, absolutePE } = timeseries.averages || {};

  const peAvg = isAverage(pe) ? pe : defaultAverages;
  const psAvg = isAverage(ps) ? ps : defaultAverages;
  const absolutePeAvg = isAverage(absolutePE) ? absolutePE : defaultAverages;

  const averages = {
    ps: {
      '1Y': psAvg.weighted_avg_1y,
      '3Y': psAvg.weighted_avg_3y,
      '5Y': psAvg.weighted_avg_5y,
      '10Y': psAvg.weighted_avg_10y,
    },
    medianPE: {
      '1Y': peAvg.weighted_avg_1y,
      '3Y': peAvg.weighted_avg_3y,
      '5Y': peAvg.weighted_avg_5y,
      '10Y': peAvg.weighted_avg_10y,
    },
    absolutePE: {
      '1Y': absolutePeAvg.weighted_avg_1y,
      '3Y': absolutePeAvg.weighted_avg_3y,
      '5Y': absolutePeAvg.weighted_avg_5y,
      '10Y': absolutePeAvg.weighted_avg_10y,
    },
  };
  let baseEarnings = 0;
  let baseRevenue = 0;
  let baseMarketCap = 0;
  const chartData = Object.keys(timeseries.pe)
    .map((d, index) => {
      const ps = Number(timeseries.ps[d]);
      const absolutePE = Number(timeseries.absolutePE[d]);

      // Since the below condition isn't clear as to what it checks (falsy). We will default to 0
      const medianPE = Number(timeseries.pe[d]) || 0;
      const earnings = Number(timeseries.earnings[d]) || 0;
      const revenue = Number(timeseries.revenue[d]) || 0;
      const marketCap = Number(timeseries.marketCap[d]) || 0;

      if (!medianPE && !earnings && !revenue && !marketCap) {
        return null;
      }
      baseEarnings =
        index === 0 || baseEarnings === 0 ? earnings : baseEarnings;
      baseRevenue = index === 0 || baseRevenue === 0 ? revenue : baseRevenue;
      baseMarketCap =
        index === 0 || baseMarketCap === 0 ? marketCap : baseMarketCap;
      return {
        time: new Date(d).getTime(),
        dateFormatted: format(new Date(d), 'E, dd MMM yyyy'),
        medianPE: medianPE || 0,
        medianPEFormatted: formatterFactor(medianPE),
        ps,
        psFormatted: formatterFactor(ps),
        absolutePE,
        absolutePEFormatted: formatterFactor(absolutePE),
        earnings,
        earningsDiff: calculateDiffPercentage(earnings, baseEarnings),
        earningsFormatted: formatCurrency(earnings, currencyISO),
        revenue,
        revenueDiff: calculateDiffPercentage(revenue, baseRevenue),
        revenueFormatted: formatCurrency(revenue, currencyISO),
        marketCap,
        marketCapDiff: calculateDiffPercentage(marketCap, baseMarketCap),
        marketCapFormatted: formatCurrency(marketCap, currencyISO),
      };
    })
    .filter((d) => d !== null) as ChartData[];

  return {
    chartData,
    industry: timeseries.industry?.name,
    market: timeseries.market.iso2,
    averages,
  };
};

export const MARKET_PERFORMANCE_QUERY_KEY = 'market-performance';
type QueryKeyType = [string, PrimaryCountryISO, number, PeriodType];

export const getMarketPerformanceKey = (
  market: PrimaryCountryISO,
  industry: number,
  period: PeriodType
): QueryKeyType => [MARKET_PERFORMANCE_QUERY_KEY, market, industry, period];

export const queryFn = async ({
  queryKey: [_, market, industryId, period],
}: QueryFunctionContext<QueryKeyType>) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const start = new Date();
  const numberOfYear = Number(period.split('Y')[0]);
  start.setFullYear(now.getFullYear() - numberOfYear);
  const response = await gqlClient.request(QUERY, {
    market: market.toUpperCase(),
    industryId,
    startTime: start.getTime(),
    endTime: now.getTime(),
  });

  return response;
};
